/**
 * Settings forms for the design-system widgets — what the Inspector ("All
 * settings") shows for the selected item. Every prop a widget publishes is
 * editable here; Heading and Card text too, because inline editing is off
 * under an uploaded design system that transforms its slot markers.
 */
import { Callout, Stack } from "@duro-app/ui";
import * as Schema from "effect/Schema";
import { useGridStore } from "@fredericrous/grid";
import { FieldName } from "@website-builder/schemas";

import {
  ICON_OPTIONS,
  IconSetting,
  SegmentedSetting,
  SelectSetting,
  SwitchSetting,
  TextAreaSetting,
  TextSetting,
  useInFormSection,
  useProps,
  type Choice,
  type IconChoice,
  type ItemRef,
} from "./controls.js";

const ALIGN: readonly Choice<"start" | "center" | "end">[] = [
  { value: "start", label: "Start" },
  { value: "center", label: "Center" },
  { value: "end", label: "End" },
];

const BUTTON_VARIANTS: readonly Choice<
  "primary" | "secondary" | "inverseSecondary" | "link" | "danger"
>[] = [
  { value: "primary", label: "Primary" },
  { value: "secondary", label: "Secondary" },
  { value: "inverseSecondary", label: "Inverse" },
  { value: "link", label: "Link" },
  { value: "danger", label: "Danger" },
];

const SIZES: readonly Choice<"default" | "small">[] = [
  { value: "default", label: "Default" },
  { value: "small", label: "Small" },
];

const WIDTHS: readonly Choice<"hug" | "fill">[] = [
  { value: "hug", label: "Hug" },
  { value: "fill", label: "Fill" },
];

const VALIGN: readonly Choice<"top" | "middle" | "bottom">[] = [
  { value: "top", label: "Top" },
  { value: "middle", label: "Middle" },
  { value: "bottom", label: "Bottom" },
];

export function ButtonSettings(item: ItemRef): React.ReactElement {
  const props = useProps(item);
  const inForm = useInFormSection(item);
  const submit = props["type"] === "submit";
  return (
    <Stack gap="md">
      <TextSetting item={item} prop="label" label="Label" />
      <TextSetting
        item={item}
        prop="href"
        label="Link"
        type="url"
        placeholder="/about or https://…"
        disabled={submit}
        description={
          submit ? "A submit button sends its form instead." : undefined
        }
      />
      <SelectSetting
        item={item}
        prop="variant"
        label="Style"
        choices={BUTTON_VARIANTS}
        fallback="primary"
      />
      <SegmentedSetting
        item={item}
        prop="size"
        label="Size"
        choices={SIZES}
        fallback="default"
      />
      <SegmentedSetting
        item={item}
        prop="width"
        label="Width"
        choices={WIDTHS}
        fallback="hug"
      />
      {/* A button that fills the cell's width has no left or right to sit at. */}
      {props["width"] !== "fill" && (
        <SegmentedSetting
          item={item}
          prop="align"
          label="Alignment"
          choices={ALIGN}
          fallback="start"
        />
      )}
      <SegmentedSetting
        item={item}
        prop="valign"
        label="Vertical"
        choices={VALIGN}
        fallback="top"
      />
      <SwitchSetting
        item={item}
        label="Submits the form"
        checked={submit}
        disabled={!inForm && !submit}
        description={inForm ? undefined : "Only inside a form section."}
        onChange={(on) =>
          on ? { type: "submit", href: undefined } : { type: undefined }
        }
      />
    </Stack>
  );
}

const LEVELS: readonly Choice<"1" | "2" | "3" | "4" | "5" | "6">[] = [
  { value: "1", label: "Heading 1" },
  { value: "2", label: "Heading 2" },
  { value: "3", label: "Heading 3" },
  { value: "4", label: "Heading 4" },
  { value: "5", label: "Heading 5" },
  { value: "6", label: "Heading 6" },
];

export function HeadingSettings(item: ItemRef): React.ReactElement {
  return (
    <Stack gap="md">
      <TextAreaSetting item={item} prop="text" label="Text" />
      <SelectSetting
        item={item}
        prop="level"
        label="Level"
        choices={LEVELS}
        fallback="2"
        toProp={(v) => Number(v)}
      />
      <SegmentedSetting
        item={item}
        prop="align"
        label="Alignment"
        choices={ALIGN}
        fallback="start"
      />
    </Stack>
  );
}

const CARD_VARIANTS: readonly Choice<
  "elevated" | "outlined" | "filled" | "interactive"
>[] = [
  { value: "outlined", label: "Outlined" },
  { value: "elevated", label: "Elevated" },
  { value: "filled", label: "Filled" },
  { value: "interactive", label: "Interactive" },
];

export function CardSettings(item: ItemRef): React.ReactElement {
  return (
    <Stack gap="md">
      <TextSetting item={item} prop="title" label="Title" />
      <TextAreaSetting item={item} prop="body" label="Body" />
      <SelectSetting
        item={item}
        prop="variant"
        label="Style"
        choices={CARD_VARIANTS}
        fallback="outlined"
      />
    </Stack>
  );
}

const INPUT_TYPES: readonly Choice<
  "text" | "search" | "email" | "url" | "tel"
>[] = [
  { value: "text", label: "Text" },
  { value: "search", label: "Search" },
  { value: "email", label: "Email" },
  { value: "url", label: "Web address" },
  { value: "tel", label: "Phone" },
];

/** The icon list preceded by a text-only "None". */
const INPUT_ICON_OPTIONS: readonly IconChoice[] = [
  { value: "none", label: "None" },
  ...ICON_OPTIONS,
];

const isFieldName = Schema.is(FieldName);

/** Why `name` can't be submitted from this form, or null. */
function useNameProblem(item: ItemRef, name: string): string | null {
  const clash = useGridStore((s) =>
    Object.entries(s.grids[item.gridId]?.items ?? {}).some(
      ([id, other]) =>
        id !== item.itemId &&
        other.contentType === "input" &&
        (other.contentProps as { name?: unknown } | undefined)?.name === name,
    ),
  );
  if (!isFieldName(name))
    return "Start with a letter; use letters, digits and _ only.";
  if (clash) return "Another field in this section already uses this name.";
  return null;
}

export function InputSettings(item: ItemRef): React.ReactElement {
  const props = useProps(item);
  const name = typeof props["name"] === "string" ? props["name"] : "";
  const problem = useNameProblem(item, name);
  return (
    <Stack gap="md">
      <TextSetting item={item} prop="label" label="Label" />
      <TextSetting item={item} prop="placeholder" label="Placeholder" />
      <SelectSetting
        item={item}
        prop="type"
        label="Kind of input"
        choices={INPUT_TYPES}
        fallback="text"
      />
      <IconSetting
        item={item}
        prop="icon"
        label="Icon"
        choices={INPUT_ICON_OPTIONS}
        fallback="none"
        toProp={(v) => (v === "none" ? undefined : v)}
      />
      <SwitchSetting
        item={item}
        label="Required"
        checked={props["required"] === true}
        onChange={(on) => ({ required: on ? true : undefined })}
      />
      <TextSetting
        item={item}
        prop="name"
        label="Field name"
        description="The name the form sends this value under."
        invalid={problem !== null}
        error={problem ?? undefined}
      />
      {problem && (
        <Callout variant="warning">
          This page can’t be published until the field name is fixed.
        </Callout>
      )}
    </Stack>
  );
}

const ICON_SIZES: readonly Choice<"sm" | "md" | "lg">[] = [
  { value: "sm", label: "Small" },
  { value: "md", label: "Medium" },
  { value: "lg", label: "Large" },
];

export function IconSettings(item: ItemRef): React.ReactElement {
  return (
    <Stack gap="md">
      <IconSetting
        item={item}
        prop="name"
        label="Icon"
        choices={ICON_OPTIONS}
        fallback="search"
      />
      <SegmentedSetting
        item={item}
        prop="size"
        label="Size"
        choices={ICON_SIZES}
        fallback="md"
      />
      <TextSetting
        item={item}
        prop="label"
        label="What it means"
        description="Read out by screen readers. Leave empty when the icon is decoration."
      />
      <SegmentedSetting
        item={item}
        prop="align"
        label="Alignment"
        choices={ALIGN}
        fallback="start"
      />
    </Stack>
  );
}
